<template>
    <ui-card class="pt-0 pl-4 pr-6 mb-5">
        <div class="has-text-centered py-2" v-if="isLoading">
            <i class="fas fa-spin fa-spinner fa-fw mr-2"></i>Loading activity...
        </div>

        <template v-else-if="activity">
            <div class="is-flex is-justify-content-space-between is-align-items-baseline is-flex-wrap-wrap mb-2">
                <h5 class="mb-0"><i class="far fa-calendar-days mr-2"></i>Study Activity</h5>
                <span class="is-size-7 has-text-grey">Last {{ months }} months &middot; click a day for details</span>
            </div>

            <p class="has-text-grey-dark mb-4" style="font-size: 0.875rem;">
                This calendar combines the time you've logged across all of your courses and
                conference records. Use it
                to catch mistakes: a timer left running, a duplicate entry, or a day that added up
                to more than you realized will stand out with a flag color. Activity that can't be
                verified may be selected for an audit, which can result in logged time being removed
                and any certification it supports being revoked.
            </p>

            <!-- Review-state banner -->
            <b-message v-if="activity.review.state === 'audit_risk'" type="is-danger" class="mb-4">
                <strong><i class="fas fa-triangle-exclamation mr-2"></i>Your account matches activity patterns selected for audit</strong>
                <p class="mb-0 mt-1">
                    {{ bannerDetail }}
                    Accounts that cannot verify their logged time may have that time removed
                    and any certification it supports revoked.
                </p>
            </b-message>
            <b-message v-else-if="activity.review.state === 'elevated'" type="is-warning" class="mb-4">
                <strong><i class="fas fa-triangle-exclamation mr-2"></i>Unusual activity on your account</strong>
                <p class="mb-0 mt-1">
                    {{ bannerDetail }}
                    Activity patterns like this are automatically flagged for review. Accounts that
                    cannot verify their logged time may have that time removed and any certification
                    it supports revoked.
                </p>
            </b-message>

            <activity-heatmap
                class="mt-5"
                :days="activity.days"
                :weeks="activity.weeks"
                :range-from="activity.range.from"
                :range-to="activity.range.to"
                :selected-date="selectedDate"
                @select="onSelect"
            />

            <!-- Legend -->
            <div class="legend mt-4 pt-3">
                <div class="lg-group">
                    <span>0h</span>
                    <span class="lg-swatch" style="background: #edece8"></span>
                    <span class="lg-swatch" style="background: #cde2fb"></span>
                    <span class="lg-swatch" style="background: #9ec5f4"></span>
                    <span class="lg-swatch" style="background: #5598e7"></span>
                    <span class="lg-swatch" style="background: #256abf"></span>
                    <span>8h</span>
                </div>
                <div class="lg-group"><span class="lg-swatch" style="background: #fab219"></span> Unusual (8&ndash;12h)</div>
                <div class="lg-group"><span class="lg-swatch" style="background: #ec835a"></span> Highly unusual (12&ndash;16h)</div>
                <div class="lg-group"><span class="lg-swatch" style="background: #d03b3b"></span> Implausible (16h+)</div>
                <div class="lg-group"><span class="lg-dotcell"></span> Overlapping sessions</div>
                <div class="lg-group"><span class="lg-ringcell"></span> During contracted hours</div>
            </div>

            <!-- Contracted schedule strip -->
            <div class="sched-strip my-5">
                <template v-if="activity.schedule.declared">
                    <span class="has-text-weight-semibold">Contracted work schedule:</span>
                    <span>{{ scheduleSummary }}<template v-if="contractYearLabel"> &middot; {{ contractYearLabel }}</template></span>
                    <span class="is-size-7 has-text-grey">declared {{ activity.schedule.effective_from | moment('MMM D, YYYY') }} &middot; changes apply going forward</span>
                    <a @click.prevent="openScheduleModal"><strong>Edit</strong></a>
                </template>
                <template v-else>
                    <span class="has-text-weight-semibold"><i class="far fa-briefcase mr-1"></i>Tell us your contracted work schedule.</span>
                    <span>Your certification may require your study time to take place outside of your contracted work hours.</span>
                    <b-button type="is-info" size="is-small" @click="openScheduleModal">Declare Schedule</b-button>
                </template>
            </div>

            <!-- Day detail -->
            <div class="day-detail mt-4" v-if="selectedDay">
                <div class="is-flex is-justify-content-space-between is-align-items-baseline is-flex-wrap-wrap">
                    <span class="has-text-weight-semibold">{{ selectedDate | moment('dddd, MMMM Do, YYYY') }}</span>
                    <span class="has-text-grey" v-if="selectedDay.data">
                        {{ fmtHours(selectedDay.data.mins) }} logged &middot; {{ groupsLabel(selectedDay.data.courses) }}
                    </span>
                </div>
                <template v-if="selectedDay.data">
                    <p class="has-text-danger has-text-weight-semibold is-size-7 mb-0 mt-1" v-if="selectedDay.data.mins > 1440">
                        Total exceeds the hours in a day.
                    </p>
                    <table class="table is-fullwidth is-narrow mt-2 mb-0">
                        <tr v-for="(entry, i) in selectedDay.data.entries" :key="i"
                            class="entry-row" :title="entry.kind === 'conference' ? 'Open this conference' : 'Open this course'"
                            @click="openEntry(entry)">
                            <td>
                                <i class="fad fa-fw mr-2 has-text-grey entry-kind-icon"
                                   :class="entry.kind === 'conference' ? 'fa-screen-users' : 'fa-chalkboard-teacher'"
                                   :title="entry.kind === 'conference' ? 'Conference' : 'Course'"></i>
                                {{ entry.title }}
                                <i v-if="entry.overlap" class="fas fa-triangle-exclamation has-text-danger ml-1" title="Overlaps another session"></i>
                                <i v-if="entry.contracted" class="far fa-briefcase ml-1 contracted-icon" title="During contracted work hours"></i>
                                <div v-if="entry.session" class="is-size-7 has-text-grey entry-session">{{ entry.session }}</div>
                            </td>
                            <td class="has-text-grey" style="white-space: nowrap;">{{ entry.start }} &ndash; {{ entry.end }}</td>
                            <td class="has-text-right" style="white-space: nowrap;">{{ fmtHours(entry.mins) }}</td>
                        </tr>
                    </table>
                    <p class="has-text-danger is-size-7 mt-2 mb-0" v-if="selectedDay.data.overlap">
                        <strong><i class="fas fa-triangle-exclamation mr-1"></i>Sessions in different courses overlap in time.</strong>
                        Overlapping study sessions cannot both be counted and are flagged for review.
                    </p>
                    <p class="is-size-7 mt-2 mb-0 contracted-note" v-if="selectedDay.data.contracted_mins > 0">
                        <strong><i class="far fa-briefcase mr-1"></i>{{ fmtHours(selectedDay.data.contracted_mins) }} of this time falls inside your contracted work schedule.</strong>
                        Time logged during contracted hours must be prep or other eligible time, and may need to be verified if your log is reviewed.
                    </p>
                </template>
                <p class="has-text-grey mt-1 mb-0" v-else>No time logged on this day.</p>
            </div>

            <contracted-schedule-modal
                :active.sync="scheduleModalOpen"
                :schedule="activity.schedule"
                @saved="onScheduleSaved"
            />
        </template>
    </ui-card>
</template>

<script>
import {axiosInstance as http} from '@/http_client';
import UiCard from '@/components/UiCard';
import ActivityHeatmap from './ActivityHeatmap';
import ContractedScheduleModal from './ContractedScheduleModal';
import {summarizeSchedule, fmtContractYear, fmtHours} from './scheduleUtil';

export default {
    name: 'StudyActivityPanel',
    components: {UiCard, ActivityHeatmap, ContractedScheduleModal},
    props: {
        months: {type: Number, default: 6},
    },
    data() {
        return {
            isLoading: true,
            activity: null,
            selectedDate: null,
            selectedDay: null,
            scheduleModalOpen: false,
        };
    },
    computed: {
        scheduleSummary() {
            return summarizeSchedule(this.activity.schedule.blocks);
        },
        contractYearLabel() {
            return fmtContractYear(this.activity.schedule.year_start, this.activity.schedule.year_end);
        },
        bannerDetail() {
            const r = this.activity.review;
            const parts = [];
            if (r.rolling_7day_max_mins >= 1440) {
                parts.push(`${this.fmtHours(r.rolling_7day_max_mins)} logged within a single 7-day span across your courses`);
            }
            if (r.critical_days > 0) parts.push(`${r.critical_days} day${r.critical_days > 1 ? 's' : ''} over 16 hours`);
            else if (r.serious_days > 0) parts.push(`${r.serious_days} day${r.serious_days > 1 ? 's' : ''} over 12 hours`);
            if (r.overlap_days > 0) parts.push('sessions in different courses that overlap in time');
            if (r.contracted_mins > 0) parts.push(`${this.fmtHours(r.contracted_mins)} logged during your contracted work schedule`);
            if (!parts.length) return 'Your recently logged time is unusually high.';
            const detail = parts.length > 1 ? parts.slice(0, -1).join(', ') + ', and ' + parts[parts.length - 1] : parts[0];
            return 'In the last 30 days: ' + detail + '.';
        },
    },
    async mounted() {
        await this.load();
    },
    methods: {
        async load() {
            this.isLoading = true;
            try {
                const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
                const {data} = await http.get('app/study-activity', {params: {tz, months: this.months}});
                this.activity = data;
            } catch (err) {
                this.activity = null;
            }
            this.isLoading = false;
        },
        fmtHours,
        groupsLabel(groups) {
            const courses = groups.filter(g => g.kind !== 'conference').length;
            const confs = groups.length - courses;
            const parts = [];
            if (courses) parts.push(courses + ' course' + (courses > 1 ? 's' : ''));
            if (confs) parts.push(confs + ' conference' + (confs > 1 ? 's' : ''));
            return parts.join(' + ') || 'no activity';
        },
        onSelect(day) {
            this.selectedDate = day.date;
            this.selectedDay = day;
        },
        openEntry(entry) {
            if (!entry.ref_id) return;
            const target = entry.kind === 'conference'
                ? {name: 'record-log', params: {registrationId: entry.ref_id}}
                : {name: 'study-log', params: {enrollmentId: entry.ref_id}};
            // clicking the course you're already viewing is a no-op, not an error
            this.$router.push(target).catch(() => {});
        },
        openScheduleModal() {
            this.scheduleModalOpen = true;
        },
        async onScheduleSaved() {
            // re-fetch so contracted-hours flags recompute against the new version
            await this.load();
        },
    },
};
</script>

<style scoped lang="scss">
.sched-strip {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 14px;
    font-size: 14px;
    border: 1px solid #ededed;
    border-radius: 8px;
    padding: 14px 16px;
}
.legend {
    display: flex;
    flex-wrap: wrap;
    gap: 12px 24px;
    align-items: center;
    font-size: 12px;
    color: #666;
    border-top: 1px solid #ededed;
}
.lg-group { display: flex; align-items: center; gap: 5px; }
.lg-swatch { width: 12px; height: 12px; border-radius: 3px; flex: none; }
/* marker samples sit on a neutral grey cell: the markers flag a day
   independently of its color, which only ever encodes the daily total */
.lg-dotcell {
    width: 12px; height: 12px; border-radius: 3px; background: #b5b5b5; position: relative; flex: none;
    &::after {
        content: ""; position: absolute; left: 4px; top: 4px;
        width: 4px; height: 4px; border-radius: 50%; background: #fff;
    }
}
.lg-ringcell {
    width: 12px; height: 12px; border-radius: 3px; background: #b5b5b5; position: relative; flex: none;
    &::before {
        content: ""; position: absolute; inset: 2px;
        border: 2px solid #fff; border-radius: 2px;
    }
}
.day-detail {
    border: 1px solid #ededed;
    border-radius: 8px;
    padding: 12px 16px;
    font-size: 14px;
}
.day-detail-empty {
    color: #999;
    text-align: center;
    border-style: dashed;
}
.day-detail table tr:last-child td {
    border-bottom: none;
}
.day-detail table td {
    padding-top: 0.65em;
    padding-bottom: 0.65em;
    vertical-align: middle;
}
.entry-row {
    cursor: pointer;
    &:hover td {
        background: #f7f9fc;
    }
}
.contracted-note {
    color: #946c00;
}
.contracted-icon {
    color: #946c00;
}
/* session subtitle aligns under the title, past the kind icon */
.entry-session {
    margin-left: 1.75rem;
}
</style>
